import type { ShipmentsTranslations } from './types'

/**
 * English translations for Shipments domain
 * Comprehensive translations covering all UI text
 */
export const enTranslations: ShipmentsTranslations = {
  // Page/Section level
  title: "Shipments",
  subtitle: "Track and manage your shipments",
  overview: "Overview",

  // Actions
  addShipment: "Add Shipment",
  editShipment: "Edit Shipment",
  deleteShipment: "Delete Shipment",

  // Search and filters
  searchPlaceholder: "Search shipments...",
  noItemsFound: "No shipments found",
  noItemsDescription: "You haven't added any shipments yet.",
  addFirst: "Add your first shipment",
  noShipments: "No Shipments",
  noShipmentsDescription: "No shipments match your current filters.",

  // Status labels
  total: "Total",
  scheduled: "Scheduled",
  loading: "Loading",
  inTransit: "In Transit",
  delivered: "Delivered",
  cancelled: "Cancelled",
  activeDeliveries: "Active Deliveries",

  // Shipment details
  shipmentDetails: "Shipment Details",
  shipmentNumber: "Shipment Number",
  order: "Order",
  quantity: "Quantity",
  productType: "Product Type",
  product: "Product",
  origin: "Origin",
  destination: "Destination",
  status: "Status",

  // Route info
  routeInfo: "Route Information",
  from: "From",
  to: "To",
  eta: "ETA",
  scheduledDate: "Scheduled Date",

  // Transport
  transport: "Transport",
  driver: "Driver",
  truck: "Truck",

  // Weighbridge
  weighbridge: "Weighbridge",
  weighbridgeGross: "Gross Weight",
  weighbridgeTare: "Tare Weight",
  weighbridgeNet: "Net Weight",
  weighbridgeTicket: "Ticket Number",
  weighbridgeMeasuredAt: "Measured At",
  weighbridgeScale: "Scale",
  trackingTimeline: "Tracking Timeline",

  // Form section
  form: {
    name: "Shipment Name",
    namePlaceholder: "Enter shipment name",
    status: "Status",
    validation: {
      nameRequired: "Shipment name is required",
    }
  },

  // Status (compatibility)
  statusLabels: {
    active: "Active",
    inactive: "Inactive",
  },

  // Messages
  messages: {
    createSuccess: "Shipment created successfully",
    updateSuccess: "Shipment updated successfully",
    deleteSuccess: "Shipment deleted successfully",
    deleteConfirm: "Are you sure you want to delete this shipment?",
  },

  // Table headers
  table: {
    name: "Name",
    status: "Status",
  },

  // Resource assignment
  resourceUpdated: "Resource updated successfully",
  resourceUpdateFailed: "Failed to update resource",
  change: "Change",
  assign: "Assign",
  validationError: "Validation Error",
  noDriverInfo: "No driver info",
  waitingForInput: "Waiting for complete input...",

  // Simulation
  simulation: {
    title: "Order Simulation",
    subtitle: "Walk through the full order lifecycle with real API calls",
    orderNumber: "Order Number",
    activityLog: "Activity Log",
    inputs: "Inputs",
    scanIn: "Scan In",
    scanOut: "Scan Out",
    recordTare: "Record Tare",
    recordGross: "Record Gross",
    pullInvoices: "Pull Invoices",
    entryGate: "Entry Gate",
    exitGate: "Exit Gate",
    entryFirstWeight: "Entry First Weight (kg)",
    entrySecondWeight: "Entry Second Weight (kg)",
    weighbridgeScan: "Weighbridge Scan",
    scanWeighbridge: "Scan In",
    weighbridgeScanComplete: "Weighbridge entry scan complete",
    tareWeight: "Tare Weight (kg)",
    grossWeight: "Gross Weight (kg)",
    invoiceSync: "Invoice Sync",
    waiting: "Waiting...",
    assignResource: "Assign Resource",
    assign: "Assign",
    joinQueue: "Join Queue (Kiosk)",
    joinQueueButton: "Join Queue",
    resourceAssigned: "Resource assigned",
    queueJoined: "Queue joined · SAP SO created",
    driverJoinsQueue: "Driver joins queue at kiosk · SAP Sales Order is created here",
    pullInvoicesDesc: "Pull invoices from SAP CPI after delivery acceptance",
    licensePlatePlaceholder: "License plate",
    driverNamePlaceholder: "Driver name",
    orderNumberPlaceholder: "ORD-2026-...",
    gateCodeEntryPlaceholder: "GATE-01",
    gateCodeExitPlaceholder: "GATE-02",
    entryComplete: "Entry scan complete",
    exitComplete: "Exit scan complete",
    qrScan: "QR Scan",
    licensePlate: "License Plate",
    qrReader: "QR Reader",
    selectQrReader: "Select QR reader...",
    noQrReadersFound: "No QR readers found for this zone",
    camera: "Camera",
    selectCamera: "Select camera...",
    noCamerasFound: "No cameras found for this zone",
    lpInputPlaceholder: "e.g. 0012-AAA",
    responseError: "Response (error)",
    responseSuccess: "Response",
    errors: {
      failedToLoadFactory: "Failed to load factory context",
      failedToLoadLifecycle: "Failed to load lifecycle",
      noOrderNumber: "No order number set",
      orderIdUnavailable: "Order ID not available — reload the order",
      stepFailed: "Step execution failed",
      refreshLifecycleFailed: "Failed to refresh lifecycle after step failure",
    },
  },

  // Shipment card
  noResourceAssigned: "No resource assigned",
  selectResource: "Select Resource",
  truckIdentification: "Truck identification",
  driverCredentials: "Driver credentials",
  selectedLabel: "Selected",
  plateLettersPlaceholder: "ABC",

  // Top-level convenience keys
  name: "Name",
  namePlaceholder: "Enter shipment name",
  nameRequired: "Shipment name is required",
  active: "Active",
  inactive: "Inactive",
  createSuccess: "Shipment created successfully",
  updateSuccess: "Shipment updated successfully",
  deleteSuccess: "Shipment deleted successfully",
  deleteConfirm: "Are you sure you want to delete this shipment?",
}
